import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrokerageLayout, SIDEBAR_BREAKPOINT_PX } from './BrokerageLayout';
import type { BrokerageNavId } from './BrokerageLayout';
import { useAuth } from '@contexts/AuthContext';
import { clearReturnAfterLogin } from '@utils/returnAfterLogin';

const SIDEBAR_STORAGE_KEY = 'saee:sidebarCollapsed';

function getInitialSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return window.innerWidth < SIDEBAR_BREAKPOINT_PX;
}

type Props = {
  pageTitle: string;
  activeNavId: BrokerageNavId;
  onBack?: () => void;
  contentKey?: string;
  children: React.ReactNode;
};

/**
 * Wraps every broker dashboard page with BrokerageLayout and handles shared
 * navigation callbacks (nav select, add brokerage, sign out) and sidebar state.
 * Sidebar collapsed state is persisted to localStorage across navigations.
 */
export function BrokerShell({ pageTitle, activeNavId, onBack, contentKey, children }: Props) {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const { clearSession } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);

  const l = lang === 'ar' ? 'ar' : 'en';

  const handleSidebarChange = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed)); } catch { /* ignore storage errors in restricted environments */ }
  }, []);

  const handleBrokerageNav = useCallback((id: BrokerageNavId) => {
    const seg =
      id === 'main' ? 'main-brokerage'
      : id === 'sub' ? 'sub-brokerage'
      : id === 'wallet' ? 'wallet'
      : 'settings';
    navigate(`/${l}/${seg}`);
  }, [l, navigate]);

  const goToNewBrokerage = useCallback(() => navigate(`/${l}/new-brokerage`), [l, navigate]);

  const handleSignOut = useCallback(() => {
    import('@services/api')
      .then(({ authLogout }) => authLogout().catch(() => {}))
      .finally(() => {
        try { window.localStorage.removeItem('saee:new-brokerage:draft:v1'); } catch { /* ignore storage errors in restricted environments */ }
        clearReturnAfterLogin();
        clearSession();
        navigate(`/${l}/signup`, { replace: true });
      });
  }, [l, clearSession, navigate]);

  return (
    <BrokerageLayout
      pageTitle={pageTitle}
      activeNavId={activeNavId}
      onNavSelect={handleBrokerageNav}
      onAddBrokerage={goToNewBrokerage}
      sidebarCollapsed={sidebarCollapsed}
      onSidebarCollapsedChange={handleSidebarChange}
      onSignOut={handleSignOut}
      onBack={onBack}
      contentKey={contentKey}
    >
      {children}
    </BrokerageLayout>
  );
}
